import { Injectable } from '@angular/core';
import { ImportColumnConfig } from '../interfaces/import-config.interface';

export interface ErrorMessageConfig {
  [errorKey: string]: string;
}

export interface ColumnErrorConfig {
  [columnKey: string]: ErrorMessageConfig;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorMessageService {
  
  // Default error messages in Spanish
  private defaultMessages: ErrorMessageConfig = {
    // Required validation
    'required': 'Esta columna es requerida',
    
    // Data type validations
    'dataType': 'El formato de datos no es válido',
    'invalidNumber': 'Debe ser un número válido',
    'invalidDate': 'Debe ser una fecha válida',
    'invalidBoolean': 'Debe ser un valor booleano válido (true/false, yes/no, 1/0)',
    
    // String validations
    'minlength': 'El texto es demasiado corto',
    'maxlength': 'El texto es demasiado largo',
    'pattern': 'El patrón utilizado en la columna no es válido',
    
    // Number validations
    'min': 'El valor es demasiado pequeño',
    'max': 'El valor es demasiado grande',
    
    // Email validation
    'email': 'Debe ser una dirección de correo válida',
    
    // Custom business validations
    'duplicate': 'Valor duplicado encontrado',
    'notFound': 'Valor no encontrado en el sistema',
    'invalidCode': 'Código no válido',
    'invalidFormat': 'Formato incorrecto',
    
    // Geographic validations
    'invalidLatitude': 'La latitud debe estar entre -90 y 90 grados',
    'invalidLongitude': 'La longitud debe estar entre -180 y 180 grados',

    // Date format validation
    'invalidDateFormat': 'Formato de fecha inválido',

    // Generic fallback
    'unknown': 'Error de validación'
  };

  private possibleApiErrorKeys: string[] = [
    'not_found',
    'apiError'
  ];

  /**
   * Gets the appropriate error message for a validation error
   * @param errorKey The validation error key (e.g., 'required', 'min', etc.)
   * @param columnConfig The column configuration
   * @param errorValue The error value (for dynamic messages)
   * @returns User-friendly error message
   */
  getErrorMessage(
    errorKey: string, 
    columnConfig: ImportColumnConfig, 
    errorValue?: any
  ): string {
    // First check for column-specific custom messages
    const columnCustomMessages = columnConfig.errorMessages;
    if (columnCustomMessages && columnCustomMessages[errorKey]) {
      return this.interpolateMessage(columnCustomMessages[errorKey], columnConfig, errorValue);
    }

    // Then check for default message overrides in column config
    if (columnConfig.defaultMessageOverrides && columnConfig.defaultMessageOverrides[errorKey]) {
      return this.interpolateMessage(columnConfig.defaultMessageOverrides[errorKey], columnConfig, errorValue);
    }

    // Use default messages
    let message = errorKey.includes('apiError') ? errorValue : this.defaultMessages[errorKey] || this.defaultMessages['unknown'];
    
    // Add dynamic information based on error type
    message = this.enhanceMessage(message, errorKey, columnConfig, errorValue);
    
    return this.interpolateMessage(message, columnConfig, errorValue);
  }

  /**
   * Enhances messages with dynamic information
   * @param message Base message
   * @param errorKey Error type
   * @param columnConfig Column configuration
   * @param errorValue Error details
   * @returns Enhanced message
   */
  private enhanceMessage(
    message: string, 
    errorKey: string, 
    columnConfig: ImportColumnConfig, 
    errorValue?: any
  ): string {
    switch (errorKey) {
      case 'required':
        return `${columnConfig.displayName} es requerido`;
      
      case 'minlength':
        const minLength = errorValue?.requiredLength || 'N/A';
        const actualLength = errorValue?.actualLength || 'N/A';
        return `${columnConfig.displayName} debe tener al menos ${minLength} caracteres (actual: ${actualLength})`;
      
      case 'maxlength':
        const maxLength = errorValue?.requiredLength || 'N/A';
        const actualLen = errorValue?.actualLength || 'N/A';
        return `${columnConfig.displayName} no puede exceder ${maxLength} caracteres (actual: ${actualLen})`;
      
      case 'min':
        const minValue = errorValue?.min || 'N/A';
        const actualMin = errorValue?.actual || 'N/A';
        return `${columnConfig.displayName} debe ser mayor o igual a ${minValue} (actual: ${actualMin})`;
      
      case 'max':
        const maxValue = errorValue?.max || 'N/A';
        const actualMax = errorValue?.actual || 'N/A';
        return `${columnConfig.displayName} debe ser menor o igual a ${maxValue} (actual: ${actualMax})`;
      
      case 'dataType':
        const expectedType = this.getDataTypeLabel(columnConfig.dataType);
        return `${columnConfig.displayName} debe ser ${expectedType}`;
      
      case 'duplicate':
        return `${columnConfig.displayName}: Valor duplicado "${errorValue}"`;

      case 'invalidDateFormat':
        // The errorValue contains { message: '...' } from the custom validator
        return errorValue?.message || `${columnConfig.displayName} debe tener formato DD/MM/AA o DD/MM/AAAA`;

      default:
        return message;
    }
  }

  /**
   * Gets user-friendly data type labels
   * @param dataType The data type
   * @returns User-friendly label
   */
  private getDataTypeLabel(dataType: string): string {
    const typeLabels: { [key: string]: string } = {
      'string': 'texto',
      'number': 'un número',
      'date': 'una fecha',
      'boolean': 'verdadero o falso'
    };
    return typeLabels[dataType] || dataType;
  }

  /**
   * Interpolates placeholders in error messages
   * @param message Message template
   * @param columnConfig Column configuration
   * @param errorValue Error details
   * @returns Interpolated message
   */
  private interpolateMessage(
    message: string, 
    columnConfig: ImportColumnConfig, 
    errorValue?: any
  ): string {
    return message
      .replace(/\{columnName\}/g, columnConfig.displayName)
      .replace(/\{columnKey\}/g, columnConfig.key)
      .replace(/\{value\}/g, errorValue?.toString() || '')
      .replace(/\{dataType\}/g, this.getDataTypeLabel(columnConfig.dataType));
  }

  /**
   * Creates a comprehensive error summary for a row
   * @param errors Array of validation errors for the row
   * @param columns Column configurations
   * @returns Formatted error summary
   */
  createRowErrorSummary(errors: any[], columns: ImportColumnConfig[]): string {
    if (errors.length === 0) return '';
    
    const errorMessages = errors.map(error => {
      const column = columns.find(col => col.key === error.column);
      if (!column) return `Error en columna "${error.column}": ${error.message}`;
      
      return this.getErrorMessage(error.errorKey || 'unknown', column, error.errorValue);
    });

    return errorMessages.join('\n');
  }

  /**
   * Gets all possible error messages for a column (for documentation/help)
   * @param columnConfig Column configuration
   * @returns Map of error keys to messages
   */
  getPossibleErrorsForColumn(columnConfig: ImportColumnConfig): { [key: string]: string } {
    const possibleErrors: { [key: string]: string } = {};
    
    // Required validation
    if (columnConfig.required) {
      possibleErrors['required'] = this.getErrorMessage('required', columnConfig);
    }
    
    // Data type validation
    possibleErrors['dataType'] = this.getErrorMessage('dataType', columnConfig);
    
    // Length validations (for strings)
    if (columnConfig.dataType === 'string') {
      possibleErrors['minlength'] = this.getErrorMessage('minlength', columnConfig, { requiredLength: 'X', actualLength: 'Y' });
      possibleErrors['maxlength'] = this.getErrorMessage('maxlength', columnConfig, { requiredLength: 'X', actualLength: 'Y' });
    }
    
    // Range validations (for numbers)
    if (columnConfig.dataType === 'number') {
      possibleErrors['min'] = this.getErrorMessage('min', columnConfig, { min: 'X', actual: 'Y' });
      possibleErrors['max'] = this.getErrorMessage('max', columnConfig, { max: 'X', actual: 'Y' });
    }
    
    return possibleErrors;
  }

  /**
   * Sets custom default messages (for application-wide customization)
   * @param customDefaults Custom default messages
   */
  setCustomDefaults(customDefaults: Partial<ErrorMessageConfig>): void {
    this.defaultMessages = { ...this.defaultMessages, ...customDefaults };
  }

  /**
   * Gets all default error messages
   * @returns Map of all default error messages
   */
  getDefaultMessages(): ErrorMessageConfig {
    return { ...this.defaultMessages };
  }
}